import { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useRouter } from 'next/router';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import {
  buildStoreEmbedUrl,
  buildStoreMapUrl,
  formatStoreAddress,
  getRetailStore,
} from '@/constants/retail-stores.constants';
import { PrimaryButtonLink } from '@/components/common/PrimaryButton';

const WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** 2024-01-01 was a Monday. Only the weekday names are read off it. */
const REFERENCE_MONDAY = Date.UTC(2024, 0, 1);
const DAY_MS = 86_400_000;

/**
 * Weekday name in the visitor's language. Taken from `Intl` rather than the
 * translation files: seven names in five locales is exactly the kind of table
 * the platform already ships correctly.
 */
const weekdayLabel = (position: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(REFERENCE_MONDAY + position * DAY_MS)
  );

/** `Tue Wed Thu Fri Sat` reads as `Di–Sa`; gaps stay separate runs. */
const formatOpeningDays = (days: string[], locale: string): string => {
  const positions = [...new Set(days.map(day => WEEK.indexOf(day)))]
    .filter(position => position >= 0)
    .sort((a, b) => a - b);

  const runs: number[][] = [];
  positions.forEach(position => {
    const current = runs.at(-1);
    if (current && position === (current.at(-1) ?? -2) + 1) current.push(position);
    else runs.push([position]);
  });

  return runs
    .map(run => {
      const first = weekdayLabel(run.at(0) as number, locale);
      if (run.length === 1) return first;
      return `${first}–${weekdayLabel(run.at(-1) as number, locale)}`;
    })
    .join(', ');
};

type Props = {
  /** Strapi `seller_id` of the landing page; decides which shop is described. */
  sellerId?: string | null;
};

/**
 * Address, opening hours and a map for the physical shop behind a landing page.
 *
 * The map is embedded plainly, without a consent gate of its own: consent for
 * Google Maps is CCM19's job, and it holds the decision for every other
 * service on the site. A second gate in here would be a second place to
 * maintain and a second thing to disagree with the cookie banner about — the
 * iframe is rendered, and CCM19 decides whether it reaches Google.
 *
 * This needs Maps to exist as an embedding in the CCM19 configuration with
 * iframe blocking switched on. Without that, CCM19 lets the iframe through
 * untouched, and the shop contacts Google unasked on every page view.
 *
 * The iframe is mounted on the client rather than rendered into the HTML,
 * which is what puts it within CCM19's reach at all. CCM19 blocks iframes from
 * two places only: a MutationObserver, and its own `appendChild`/`insertBefore`
 * on `Node.prototype`. An iframe delivered in the server-rendered HTML passes
 * both — React hydrates that node in place instead of inserting it — and the
 * browser has requested Google off the `src` long before the `async` CCM19
 * script runs. Mounting it here routes the node through the insertion CCM19
 * watches, before it has a `src` the browser could act on.
 *
 * And it waits for CCM19 to be there, because that insertion is only watched
 * once CCM19 has patched `Node.prototype`. Hydration can win that race against
 * an `async` script, and an iframe mounted a moment too early is one CCM19
 * never sees. Where CCM19 never arrives at all — a blocker ate it — the map
 * stays a blank panel: no consent manager means no consent, and the address
 * and directions link next to it work without either.
 */
export default function StoreMap({ sellerId }: Props): JSX.Element | null {
  const { locale = 'en' } = useRouter();
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const [consentManagerReady, setConsentManagerReady] = useState(false);

  useEffect(() => {
    // `window.CCM` is published at the end of CCM19's own start-up, after it
    // has installed the blocking it needs to do — so its presence is the
    // signal that inserting an iframe is safe, not merely that the script tag
    // has been seen.
    if (window.CCM) {
      setConsentManagerReady(true);
      return;
    }

    const onLoaded = () => setConsentManagerReady(true);
    window.addEventListener('ccm19WidgetLoaded', onLoaded);
    return () => window.removeEventListener('ccm19WidgetLoaded', onLoaded);
  }, []);

  const store = getRetailStore(sellerId);
  if (!store) return null;

  const address = formatStoreAddress(store);
  // Keyless embed: the documented Embed API would need its own key and billing
  // in the Google Cloud project for a picture that, with a single location,
  // looks the same.
  const embedUrl = buildStoreEmbedUrl(store, locale);

  return (
    <Container className="tw-my-8">
      <div className="tw-overflow-hidden tw-rounded-[20px] tw-bg-customGray">
        <div className="tw-flex tw-flex-col md:tw-flex-row">
          <div className="tw-flex tw-flex-col tw-gap-4 tw-p-[30px] md:tw-w-2/5">
            <h2 className="tw-mb-0 tw-text-2xl tw-font-medium">
              {translate(CommonEnum.visit_us_in_store)}
            </h2>

            <address className="tw-mb-0 tw-not-italic tw-leading-relaxed">
              <span className="tw-block tw-font-medium">{store.name}</span>
              <span className="tw-block">{store.streetAddress}</span>
              <span className="tw-block">
                {store.postalCode} {store.addressLocality}
              </span>
            </address>

            <div className="tw-leading-relaxed">
              <span className="tw-block tw-font-medium">{translate(CommonEnum.opening_hours)}</span>
              <span>
                {formatOpeningDays(store.openingDays, locale)} {store.opens}–{store.closes}
              </span>
            </div>

            <PrimaryButtonLink
              href={buildStoreMapUrl(store)}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-w-fit tw-px-[40px]"
            >
              {translate(CommonEnum.get_directions)}
            </PrimaryButtonLink>
          </div>

          <div className="tw-min-h-[320px] tw-flex-1">
            {consentManagerReady ? (
              <iframe
                title={`${store.name} — ${address}`}
                src={embedUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="tw-h-full tw-min-h-[320px] tw-w-full tw-border-0"
              />
            ) : (
              // Holds the space the map or CCM19's placeholder takes, so the
              // card does not jump on mount.
              <div className="tw-h-full tw-min-h-[320px] tw-w-full tw-bg-[#DEDFE2]" />
            )}
          </div>
        </div>
      </div>
    </Container>
  );
}
